import React, { useCallback, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormComponentProps } from 'types/components';
import { StyledDiv, Text } from 'up-ops-web-shared-components/lib';

import FileUpload, {
  FileUploadProps,
} from 'up-ops-web-shared-components/lib/components/file-upload';

type FormFileUploadProps = FormComponentProps &
  Omit<FileUploadProps, 'onFilesChange'>;

const FormFileUpload = ({
  defaultValue,
  name,
  rules,
  ...props
}: FormFileUploadProps) => {
  const { control, formState } = useFormContext();
  const { t } = useTranslation();
  const [internalError, setInternalError] = useState<string>('');

  const getRejectionMessage = useCallback(
    (fileErrorCode: string) => {
      switch (fileErrorCode) {
        case 'file-invalid-type':
          return t<string>('validation.uploadFileTypes');
        default:
          return t<string>('validation.uploadInvalid');
      }
    },
    [t]
  );

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field, fieldState }) => {
        const { onChange, value } = field;
        const { error, invalid } = fieldState;
        return (
          <>
            <FileUpload
              disabled={formState.isSubmitting}
              error={invalid || !!internalError}
              files={value}
              hint={error?.message || internalError}
              messages={{
                browseLabel: t<string>('labels.browseFiles'),
                dragAndDropLabel: t<string>('labels.dragAndDrop'),
                or: t<string>('common.or'),
              }}
              onFilesChange={onChange}
              onFilesRejected={(rejections) => {
                setInternalError(
                  rejections.length
                    ? getRejectionMessage(rejections[0].errors[0].code)
                    : ''
                );
              }}
              {...props}
            />
            {!internalError && (
              <>
                <StyledDiv marginTop="xs" />
                <Text>{t('validation.uploadFileTypes')}</Text>
              </>
            )}
          </>
        );
      }}
      rules={rules}
    />
  );
};

export default FormFileUpload;
